import { useState } from "react"
import { Button, Input, Textarea } from "@aix/ui"
import { X, Package } from "lucide-react"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"
import { useNavigate } from "@tanstack/react-router"

interface CreateTemplateFromCoachModalProps {
  isOpen: boolean
  onClose: () => void
  coach: {
    id: string
    name: string
    icon: string
    description: string | null
    type: "pillar" | "side-quest"
    tone: "tough-love" | "chill" | "science-y"
  }
}

export default function CreateTemplateFromCoachModal({ isOpen, onClose, coach }: CreateTemplateFromCoachModalProps) {
  const navigate = useNavigate()
  const [templateName, setTemplateName] = useState(`${coach.name} Template`)
  const [category, setCategory] = useState<"health" | "wealth" | "relationships" | "growth" | "career" | "project">(
    "health",
  )
  const [description, setDescription] = useState(coach.description || "")
  const [isPublic, setIsPublic] = useState(false)
  const [changelog, setChangelog] = useState("")

  const createMutation = trpc.coachTemplates.createFromCoach.useMutation()

  const handleCreate = async () => {
    try {
      if (!templateName.trim()) {
        toast.error("Template name is required")
        return
      }

      const result = await createMutation.mutateAsync({
        coachId: coach.id,
        templateName: templateName.trim(),
        category,
        description: description.trim() || undefined,
        isPublic,
        version: "1.0.0",
        changelog: changelog.trim() || undefined,
      })

      toast.success(result.message || "Template created successfully!")
      onClose()

      // Navigate to the templates tab
      navigate({
        to: "/coach-os",
        // @ts-ignore
        search: (prev) => ({ ...prev, tab: "templates" }),
      })
    } catch (error: any) {
      toast.error(error.message || "Failed to create template")
    }
  }

  if (!isOpen) return null

  const categories = [
    { value: "health", label: "🏃 Health", description: "Fitness, nutrition, longevity" },
    { value: "wealth", label: "💰 Wealth", description: "Business, investing, finance" },
    { value: "relationships", label: "❤️ Relationships", description: "Family, friends, dating" },
    { value: "growth", label: "🌱 Growth", description: "Learning, skills, personal development" },
    { value: "career", label: "💼 Career", description: "Job, promotions, networking" },
    { value: "project", label: "🎯 Project", description: "Specific goals or projects" },
  ] as const

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border-4 border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b-2 border-border p-4 flex items-center justify-between bg-secondary sticky top-0">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-bold text-primary">Create Template from Coach</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-background rounded-md transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Coach Preview */}
        <div className="p-4 border-b-2 border-border bg-secondary/50">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{coach.icon}</span>
            <div>
              <h3 className="font-bold text-lg">{coach.name}</h3>
              <p className="text-sm text-muted-foreground">
                {coach.type === "pillar" ? "🏛️ Pillar Habit" : "🎯 Side Quest"} • {coach.tone} tone
              </p>
            </div>
          </div>
          {coach.description && <p className="text-sm text-muted-foreground mt-2">{coach.description}</p>}
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Convert this coach into a reusable template. The template will include all the coach&apos;s configuration
            (goals, trackables, intake questions, etc.) so you can easily create similar coaches in the future.
          </p>

          {/* Template Name */}
          <div>
            <label className="text-sm font-medium block mb-1">Template Name *</label>
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Half Marathon Training Template"
              className="w-full"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium block mb-2">Category *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategory(cat.value)}
                  className={`p-3 rounded-md text-left border-2 transition-colors ${
                    category === cat.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:bg-secondary"
                  }`}
                >
                  <div className="font-medium text-sm">{cat.label}</div>
                  <div className="text-xs opacity-80 mt-0.5">{cat.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium block mb-1">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this template is for and who should use it..."
              className="w-full"
              rows={3}
            />
          </div>

          {/* Changelog */}
          <div>
            <label className="text-sm font-medium block mb-1">Changelog (optional)</label>
            <Input
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              placeholder={`Created from coach "${coach.name}"`}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">
              What makes this template special or what changes were made
            </p>
          </div>

          {/* Public Toggle */}
          <div className="border-2 border-border rounded-lg p-4 bg-secondary/30">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="mt-1 w-4 h-4"
              />
              <div className="flex-1">
                <div className="font-medium text-sm">Make template public</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {isPublic
                    ? "✅ This template will be visible to all users in the template library"
                    : "🔒 This template will be private and only visible to you"}
                </div>
              </div>
            </label>
          </div>

          {/* Info Box */}
          <div className="bg-secondary/50 border-2 border-border rounded-lg p-3">
            <p className="text-xs text-muted-foreground">
              💡 <strong>What gets included:</strong> The template will preserve all the coach&apos;s configuration
              including goals, trackables, intake questions, planning parameters, and expert tips. Your personal data
              (actual check-ins, progress, etc.) will NOT be included.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t-2 border-border p-4 flex gap-3 justify-end bg-secondary">
          <Button onClick={onClose} variant="outline" disabled={createMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            className="bg-primary text-primary-foreground"
            disabled={!templateName.trim() || createMutation.isLoading}
            loading={createMutation.isLoading}
          >
            {createMutation.isLoading ? "Creating..." : "Create Template"}
          </Button>
        </div>
      </div>
    </div>
  )
}
